import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { checkAndApplyPendingDiscountCode } from '@theme/auto-apply-coupon';
import { DeliveryEvents } from '@theme/delivery-core';
import { removeCartSellingPlans } from '@theme/thuysvers-cart-selling-plan-utils';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(DeliveryEvents.POSTAL_CODE_CHANGED, this.#handlePostalCodeChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(DeliveryEvents.POSTAL_CODE_CHANGED, this.#handlePostalCodeChanged);
  }

  #handleCartAdd = async () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.handleCartButtonClick(true);
      void this.#runSellingPlanOpenFlow();
    }

    // Check for and apply any pending discount codes when cart becomes available
    await checkAndApplyPendingDiscountCode();
  };

  /**
   * Handles postal code change event to update checkout links
   * @param {CustomEvent} event - The postal code changed event
   */
  #handlePostalCodeChanged = (event) => {
    try {
      const { postalCode } = event.detail;
      
      // Use shared utility to update checkout links in the cart drawer
      /** @type {any} */ (window).CheckoutLinkUpdater.updateCheckoutLinks(postalCode, this);
      
      // Also update checkout links in the cart summary (outside the drawer)
      /** @type {any} */ (window).CheckoutLinkUpdater.updateCheckoutLinks(postalCode, document);

    } catch (error) {
      console.error('❌ [CART-DRAWER] Error handling postal code change:', error);
    }
  };

  open() {
    this.showDialog();
    this.handleCartButtonClick(true);
    // If cart has subscription selling plans, ask before stripping (same choice pattern as subscription quit modal)
    void this.#runSellingPlanOpenFlow();
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  /** Prevents two concurrent removal runs from stacking loaders. */
  #sellingPlanRemovalInProgress = false;

  /**
   * After opening the drawer: if any line has a selling plan, show the choice modal.
   * Stay → keep subscription pricing; confirm → strip plans (previous automatic behaviour).
   */
  async #runSellingPlanOpenFlow() {
    const hasPlans = await this.#cartHasSellingPlans();
    if (!hasPlans) return;
    const choice = await this.#waitForSellingPlanModalChoice();
    if (choice === 'strip') {
      await this.#removeCartSellingPlansIfPresent();
      window.location.reload();
    }
  }

  /**
   * @returns {Promise<boolean>}
   */
  async #cartHasSellingPlans() {
    try {
      const cartUrl =
        typeof Theme !== 'undefined' && Theme?.routes?.cart_url
          ? `${String(Theme.routes.cart_url).replace(/\/?$/, '')}.js`
          : '/cart.js';
      const res = await fetch(cartUrl);
      if (!res.ok) return false;
      const cart = await res.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      return items.some((/** @type {any} */ item) => item?.selling_plan_allocation != null);
    } catch {
      return false;
    }
  }

  /**
   * @returns {Promise<'keep'|'strip'>}
   */
  #waitForSellingPlanModalChoice() {
    const modal = this.querySelector('[data-cart-selling-plan-modal]');
    if (!(modal instanceof HTMLElement)) return Promise.resolve('keep');

    return new Promise((resolve) => {
      let settled = false;
      const finish = (/** @type {'keep'|'strip'} */ choice) => {
        if (settled) return;
        settled = true;
        modal.removeEventListener('click', onClick);
        document.removeEventListener('keydown', onKey);
        this.#hideSellingPlanModal();
        resolve(choice);
      };

      /** @param {KeyboardEvent} e */
      const onKey = (e) => {
        if (e.key === 'Escape') finish('keep');
      };

      /** @param {MouseEvent} e */
      const onClick = (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        if (t.closest('[data-cart-selling-plan-confirm]')) {
          e.preventDefault();
          finish('strip');
        } else if (t.closest('[data-cart-selling-plan-dismiss]')) {
          e.preventDefault();
          finish('keep');
        }
      };

      document.addEventListener('keydown', onKey);
      modal.addEventListener('click', onClick);
      this.#showSellingPlanModal();

      const focusTarget =
        modal.querySelector('.tv-quit-modal__btn') || modal.querySelector('.tv-quit-modal__dialog');
      if (focusTarget instanceof HTMLElement) {
        try {
          focusTarget.focus();
        } catch {
          // ignore
        }
      }
    });
  }

  #showSellingPlanModal() {
    const modal = this.querySelector('[data-cart-selling-plan-modal]');
    if (!(modal instanceof HTMLElement)) return;
    modal.hidden = false;
    modal.classList.remove('tv-quit-modal--hidden');
    modal.setAttribute('aria-hidden', 'false');
  }

  #hideSellingPlanModal() {
    const modal = this.querySelector('[data-cart-selling-plan-modal]');
    if (!(modal instanceof HTMLElement)) return;
    modal.classList.add('tv-quit-modal--hidden');
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;
  }

  /**
   * In-drawer overlay (same `.load` spinner as global loader — see snippet cart-drawer).
   * @param {boolean} visible
   */
  #setCartSellingPlanLoaderVisible(visible) {
    const el = this.querySelector('.cart-drawer__loader');
    if (!(el instanceof HTMLElement)) return;
    el.hidden = !visible;
    el.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  /**
   * After shopper confirms: clear the entire cart (POST /cart/clear.js), same pattern
   * as the subscription quit modal — not per-line selling_plan removal.
   * Shows the in-cart overlay while the request runs.
   */
  async #removeCartSellingPlansIfPresent() {
    if (this.#sellingPlanRemovalInProgress) return;
    this.#sellingPlanRemovalInProgress = true;
    this.#setCartSellingPlanLoaderVisible(true);
    try {
      await removeCartSellingPlans(() => this.#getCartSectionsToUpdate());
    } finally {
      this.#sellingPlanRemovalInProgress = false;
      this.#setCartSellingPlanLoaderVisible(false);
    }
  }

  /**
   * @returns {Set<string>}
   */
  #getCartSectionsToUpdate() {
    const sectionsToUpdate = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionsToUpdate.add(el.dataset.sectionId);
    });
    const subscriptionModalId = document
      .querySelector('[data-tv-subscription-cart-modal][data-section-id]')
      ?.getAttribute('data-section-id');
    if (subscriptionModalId) sectionsToUpdate.add(subscriptionModalId);
    return sectionsToUpdate;
  }

  close() {
    this.closeDialog();
    this.handleCartButtonClick(false);
  }

  openDeliveryDrawer() {
    // Close the cart drawer first
    this.close();
    
    // Use the centralized delivery times drawer opening function
    if (/** @type {any} */ (window).combinedDrawers?.openDelivery) {
      /** @type {any} */ (window).combinedDrawers.openDelivery();
    } else {
      // Fallback to direct access
      const deliveryDrawer = document.querySelector('delivery-times-drawer-component');
      if (deliveryDrawer) {
        /** @type {any} */ (deliveryDrawer).open();
      }
    }
  }

  /**
   * @param {boolean} isOpen
   */
  handleCartButtonClick(isOpen) {
    const collectionCartButton = document.querySelectorAll('.collection-cart-button');
    if (collectionCartButton.length > 0) {
      for (const button of collectionCartButton) {
        button.classList.toggle('active', isOpen);
        button.setAttribute('aria-expanded', String(isOpen));
      }
    }
  }

  /**
   * Handles next button click using global utility
   * @param {Event} event - The click event
   */
  handleNextButton = async (event) => {
    // Find the actual instance of the custom collection footer component in the DOM
    const footerComponent = document.querySelector('custom-collection-footer-component') || document.querySelector('custom-collection-subscription-footer');
    
    if (footerComponent && 'handleCartNextButton' in footerComponent && typeof /** @type {any} */ (footerComponent).handleCartNextButton === 'function') {
      await /** @type {any} */ (footerComponent).handleCartNextButton(event);
    } else {
      const target = /** @type {HTMLElement | null} */ (event?.target ?? null);
      const checkoutUrl = target?.getAttribute('data-checkout-url');
      const weekmenuUrl = target?.getAttribute('data-weekmenu-url');
      if (window.location.pathname.includes('collection')) {
        if (weekmenuUrl) {
          window.location.href = weekmenuUrl;
        }
      } else {
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        }
      }
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
